import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getUserRole } from '@/lib/firestoreRoles';
import { AppRole, UserRole } from '@/types/roles';
import app from '@/lib/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface RoleAuthState {
  role: AppRole | null;
  isAdmin: boolean;
  isModerator: boolean;
  isAdminOrModerator: boolean;
  isEnabled: boolean;
  isChecking: boolean;
  userRole: UserRole | null;
  refreshRole: () => Promise<void>;
}

// Hardcoded admin emails (fallback for Firebase-based admin check)
const ADMIN_EMAILS = ['madhavgargjan20@gmail.com'];
const functions = getFunctions(app);
const claimStaffAccessFn = httpsCallable(functions, 'claimStaffAccess');
const claimStudentAccessFn = httpsCallable(functions, 'claimStudentAccess');

const inFlightClaimByUser = new Map<string, Promise<boolean>>();
const claimCooldownByUser = new Map<string, number>();

export function useRoleAuth(): RoleAuthState {
  const { user } = useAuth();
  const [role, setRole] = useState<AppRole | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  const checkRoleStatus = useCallback(async () => {
    if (!user?.uid || !user?.email) {
      setRole(null);
      setIsEnabled(false);
      setIsChecking(false);
      setUserRole(null);
      return;
    }

    try {
      // Check Firestore roles collection
      let result = await getUserRole(user.uid, user.email);

      if (result.role) {
        // Self-heal partially linked staff/student role docs.
        if (result.role === 'consultant_staff' && !result.userRole?.consultantId) {
          try {
            const claimKey = `staff-self-heal:${user.uid}:${user.email.toLowerCase()}`;
            let claimPromise = inFlightClaimByUser.get(claimKey);
            if (!claimPromise) {
              claimPromise = claimStaffAccessFn({})
                .then((res) => Boolean((res.data as any)?.claimed))
                .catch(() => false)
                .finally(() => inFlightClaimByUser.delete(claimKey));
              inFlightClaimByUser.set(claimKey, claimPromise);
            }
            await claimPromise;
            result = await getUserRole(user.uid, user.email);
          } catch {
            // Ignore and continue with existing role state.
          }
        }
        if (result.role === 'student' && (!result.userRole?.consultantId || !result.userRole?.studentDocId)) {
          try {
            const claimKey = `student-self-heal:${user.uid}:${user.email.toLowerCase()}`;
            let claimPromise = inFlightClaimByUser.get(claimKey);
            if (!claimPromise) {
              claimPromise = claimStudentAccessFn({})
                .then((res) => Boolean((res.data as any)?.claimed))
                .catch(() => false)
                .finally(() => inFlightClaimByUser.delete(claimKey));
              inFlightClaimByUser.set(claimKey, claimPromise);
            }
            await claimPromise;
            result = await getUserRole(user.uid, user.email);
          } catch {
            // Ignore and continue with existing role state.
          }
        }
        setRole(result.role);
        setIsEnabled(result.enabled);
        setUserRole(result.userRole);
      } else {
        // Fallback to hardcoded admin check
        if (ADMIN_EMAILS.includes(user.email.toLowerCase())) {
          setRole('admin');
          setIsEnabled(true);
          setUserRole({
            id: 'hardcoded',
            user_id: user.uid,
            email: user.email,
            role: 'admin',
            enabled: true,
            assigned_by: 'system',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        } else {
          // No role doc yet: try auto-claim (staff first, then student) by email.
          const claimKey = `${user.uid}:${user.email.toLowerCase()}`;
          const cooldownUntil = claimCooldownByUser.get(claimKey) ?? 0;

          if (Date.now() < cooldownUntil) {
            setRole(null);
            setIsEnabled(false);
            setUserRole(null);
            return;
          }

          let claimPromise = inFlightClaimByUser.get(claimKey);
          if (!claimPromise) {
            claimPromise = (async () => {
              let claimed = false;
              try {
                const staffClaim = await claimStaffAccessFn({});
                claimed = Boolean((staffClaim.data as any)?.claimed);
              } catch {
                // Ignore
              }

              if (!claimed) {
                try {
                  const studentClaim = await claimStudentAccessFn({});
                  claimed = Boolean((studentClaim.data as any)?.claimed);
                } catch {
                  // Ignore
                }
              }

              return claimed;
            })().finally(() => {
              inFlightClaimByUser.delete(claimKey);
            });
            inFlightClaimByUser.set(claimKey, claimPromise);
          }

          const claimed = await claimPromise;

          if (claimed) {
            const refreshed = await getUserRole(user.uid, user.email);
            setRole(refreshed.role);
            setIsEnabled(refreshed.enabled);
            setUserRole(refreshed.userRole);
          } else {
            claimCooldownByUser.set(claimKey, Date.now() + 2 * 60 * 1000);
            setRole(null);
            setIsEnabled(false);
            setUserRole(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to check role status:', err);
      // Fallback to hardcoded admin check
      if (ADMIN_EMAILS.includes(user.email.toLowerCase())) {
        setRole('admin');
        setIsEnabled(true);
      } else {
        setRole(null);
        setIsEnabled(false);
      }
      setUserRole(null);
    } finally {
      setIsChecking(false);
    }
  }, [user?.uid, user?.email]);

  useEffect(() => {
    checkRoleStatus();
  }, [checkRoleStatus]);

  const isAdmin = role === 'admin' && isEnabled;
  const isModerator = role === 'moderator' && isEnabled;
  const isAdminOrModerator = isAdmin || isModerator;

  return {
    role,
    isAdmin,
    isModerator,
    isAdminOrModerator,
    isEnabled,
    isChecking,
    userRole,
    refreshRole: checkRoleStatus,
  };
}
